import React, { useEffect, useState } from 'react';
import { assessmentApi, organizationApi, frameworksApi } from '../../services/api';
import CustomSelect from '../common/CustomSelect';
import { useInstanceMode } from '../../providers/InstanceModeProvider';
import { useT } from '../../providers/I18nProvider';

function AssessmentEditSidebar({ isOpen, onClose, assessment, onSaved, onNotify }) {
  const t = useT();
  const [loading, setLoading] = useState(false);
  const [organizations, setOrganizations] = useState([]);
  const [availableOrganizations, setAvailableOrganizations] = useState([]);
  const [frameworks, setFrameworks] = useState([]);
  const role = localStorage.getItem('role');
  const { pureOES } = useInstanceMode();
  const isGlobalAdmin = role === 'global_admin';
  const isAssessmentAdmin = role === 'assessment_admin';
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    start_date: '',
    end_date: '',
    status: 'draft',
    organization_id: '',
    criticality_guideline: '',
    posture_framework: '',
    framework_id: '',
    estimated_duration_weeks: '',
    estimated_hours: '',
    notes: ''
  });

  const criticalityGuidelines = [
    { value: '', label: 'Select guideline' },
    { value: 'NIS2', label: 'NIS2 Directive' },
    { value: 'EU CER', label: 'EU CER Directive' },
    { value: 'Danish', label: 'Danish Criticality Model' },
    { value: 'Estonian', label: 'Estonian Criticality Model' },
    { value: 'Other', label: 'Other' },
  ];

  const postureFrameworkOptions = [
    { value: '', label: 'Select framework' },
    ...frameworks.map((f) => ({ value: String(f.id), label: f.name })),
  ];

  const toDateInput = (value) => {
    if (!value) return '';
    try {
      return new Date(value).toISOString().slice(0, 10);
    } catch (e) {
      return value;
    }
  };

  useEffect(() => {
    if (isOpen) {
      fetchOrganizations();
      fetchFrameworks();
    }
  }, [isOpen]);

  const fetchFrameworks = async () => {
    try {
      const response = await frameworksApi.getAll(0, 100);
      setFrameworks(response.data || []);
    } catch (error) {
      console.error('Error fetching frameworks:', error);
    }
  };

  const fetchOrganizations = async () => {
    try {
      const response = await organizationApi.getAll();
      setOrganizations(response.data);
      // For organization admins and assessment admins, the API already filters to only their organizations
      // For global admins, they see all organizations
      setAvailableOrganizations(response.data);
      
      // If user is org admin or assessment admin and has only one organization, pre-select it when creating
      if (!assessment && !isGlobalAdmin && response.data.length === 1) {
        setFormData(prev => ({ ...prev, organization_id: String(response.data[0].id) }));
      }
    } catch (error) {
      console.error('Error fetching organizations:', error);
    }
  };

  useEffect(() => {
    if (assessment) {
      setFormData({
        name: assessment.name || '',
        description: assessment.description || '',
        start_date: toDateInput(assessment.start_date),
        end_date: toDateInput(assessment.end_date),
        status: assessment.status || 'draft',
        organization_id: assessment.organization_id ? String(assessment.organization_id) : '',
        criticality_guideline: assessment.criticality_guideline || '',
        posture_framework: assessment.posture_framework || '',
        framework_id: assessment.framework_id != null ? String(assessment.framework_id) : '',
        estimated_duration_weeks: assessment.estimated_duration_weeks ?? '',
        estimated_hours: assessment.estimated_hours ?? '',
        notes: assessment.notes || ''
      });
    } else {
      setFormData({
        name: '',
        description: '',
        start_date: '',
        end_date: '',
        status: 'draft',
        organization_id: '',
        criticality_guideline: '',
        posture_framework: '',
        framework_id: '',
        estimated_duration_weeks: '',
        estimated_hours: '',
        notes: ''
      });
    }
  }, [assessment, isOpen]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      // Validate organization_id is provided
      if (!formData.organization_id) {
        if (onNotify) onNotify('error', 'Please select an organization');
        setLoading(false);
        return;
      }

      const selectedFramework = frameworks.find((f) => String(f.id) === formData.framework_id);
      const payload = {
        ...formData,
        organization_id: parseInt(formData.organization_id, 10),
        criticality_guideline: formData.criticality_guideline || null,
        posture_framework: selectedFramework ? selectedFramework.name : (formData.posture_framework || null),
        framework_id: formData.framework_id ? parseInt(formData.framework_id, 10) : null,
        estimated_duration_weeks: formData.estimated_duration_weeks
          ? parseInt(formData.estimated_duration_weeks, 10)
          : null,
        estimated_hours: formData.estimated_hours
          ? parseInt(formData.estimated_hours, 10)
          : null,
        notes: formData.notes || null,
      };

      if (assessment) {
        await assessmentApi.update(assessment.id, payload);
        if (onNotify) onNotify('success', 'Assessment updated successfully!');
      } else {
        await assessmentApi.create(payload);
        if (onNotify) onNotify('success', 'Assessment created successfully!');
      }

      if (onSaved) onSaved();
      onClose();
    } catch (error) {
      if (onNotify) onNotify('error', 'Failed to save assessment');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <>
      <div
        className="fixed inset-0 bg-slate-900 bg-opacity-30 z-40 transition-opacity"
        onClick={onClose}
      ></div>

      <div className="fixed right-0 top-0 bottom-0 w-full max-w-3xl bg-white shadow-xl z-50 overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-slate-200 px-6 py-4 z-10">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold text-eucnet-navy">
              {assessment ? t('assessment.editAssessmentLabel', { name: assessment.name }) : t('assessment.createNewAssessment')}
            </h2>
            <button
              onClick={onClose}
              className="p-2 rounded-lg hover:bg-slate-100 text-slate-600"
            >
              <span className="text-xl leading-none">&times;</span>
            </button>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">
              {t('assessment.assessmentName')} *
            </label>
            <input
              type="text"
              name="name"
              required
              value={formData.name}
              onChange={handleInputChange}
              className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-eucnet-sky focus:border-transparent"
              placeholder="e.g., Dominican Republic CII Assessment 2026"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">
              {t('common.description')}
            </label>
            <textarea
              name="description"
              value={formData.description}
              onChange={handleInputChange}
              rows="3"
              className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-eucnet-sky focus:border-transparent"
              placeholder="Brief description of this assessment project"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                {t('assessment.startDate')} *
              </label>
              <input
                type="date"
                name="start_date"
                required
                value={formData.start_date}
                onChange={handleInputChange}
                className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-eucnet-sky focus:border-transparent"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                {t('assessment.endDate')} *
              </label>
              <input
                type="date"
                name="end_date"
                required
                value={formData.end_date}
                onChange={handleInputChange}
                className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-eucnet-sky focus:border-transparent"
              />
            </div>
          </div>

          <div className={`grid ${pureOES ? 'grid-cols-1' : 'grid-cols-2'} gap-4`}>
            {!pureOES && (
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  {t('assessment.organization')} *
                </label>
                <CustomSelect
                  name="organization_id"
                  value={formData.organization_id}
                  onChange={handleInputChange}
                  options={[
                    { value: '', label: 'Select organization' },
                    ...availableOrganizations.map(org => ({ value: String(org.id), label: org.name }))
                  ]}
                  placeholder="Select organization"
                  disabled={!isGlobalAdmin && (assessment ? assessment.organization_id : true)}
                />
              </div>
            )}
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                {t('common.status')}
              </label>
              <CustomSelect
                name="status"
                value={formData.status}
                onChange={handleInputChange}
                options={[
                  { value: 'draft', label: 'Draft' },
                  { value: 'active', label: 'Active' },
                  { value: 'completed', label: 'Completed' },
                  { value: 'archived', label: 'Archived' }
                ]}
                placeholder="Select status"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                {t('assessment.criticalityGuideline')}
              </label>
              <CustomSelect
                name="criticality_guideline"
                value={formData.criticality_guideline}
                onChange={handleInputChange}
                options={criticalityGuidelines}
                placeholder="Select guideline"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                {t('assessment.controlFramework')}
              </label>
              <CustomSelect
                name="framework_id"
                value={formData.framework_id}
                onChange={handleInputChange}
                options={postureFrameworkOptions}
                placeholder="Select framework"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                {t('assessment.estimatedDuration')}
              </label>
              <input
                type="number"
                min="0"
                name="estimated_duration_weeks"
                value={formData.estimated_duration_weeks}
                onChange={handleInputChange}
                className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-eucnet-sky focus:border-transparent"
                placeholder="e.g., 12"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                {t('assessment.estimatedHours')}
              </label>
              <input
                type="number"
                min="0"
                name="estimated_hours"
                value={formData.estimated_hours}
                onChange={handleInputChange}
                className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-eucnet-sky focus:border-transparent"
                placeholder="e.g., 240"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">
              {t('assessment.notes')}
            </label>
            <textarea
              name="notes"
              value={formData.notes}
              onChange={handleInputChange}
              rows="3"
              className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-eucnet-sky focus:border-transparent"
              placeholder="Add any additional notes or context..."
            />
          </div>

          <div className="flex justify-end space-x-3 pt-4 border-t border-slate-200">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium border border-slate-300 text-slate-700 rounded-lg hover:bg-slate-50 transition-colors"
            >
              {t('common.cancel')}
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-4 py-2 text-sm font-medium bg-eucnet-sky text-white rounded-lg hover:bg-eucnet-navy transition-colors disabled:opacity-50"
            >
              {loading ? t('common.saving') : assessment ? t('settings.saveChanges') : t('assessment.createAssessment')}
            </button>
          </div>
        </form>
      </div>
    </>
  );
}

export default AssessmentEditSidebar;
